import * as Sentry from '@sentry/nextjs'

export interface ExtraContext {
  statusCode?: number
  pathname?: string
  digest?: string
  platform?: 'pulsz' | 'bingo'
}

export const logErrorToSentry = (error: unknown, type: 'http_500' | 'unexpected', extra: ExtraContext = {}) => {
  // ensure we have a proper Error object
  const originalErr = error instanceof Error ? error : new Error(String(error))

  // grey subtitle in Sentry message
  const sentryErr = new Error(originalErr.message)

  // copy the original stack trace to preserve where the error actually occurred
  sentryErr.stack = originalErr.stack

  // blue title in Sentry message
  sentryErr.name =
    type === 'http_500' ? `Server Internal Error - ${extra.platform}` : `Unexpected Error - ${extra.platform}`

  Sentry.withScope((scope) => {
    scope.setTag('error_type', type)
    // extra info tags on the right panel inside of Sentry Issue
    if (extra.platform) {
      scope.setTag('platform', extra.platform)
    }
    if (extra.statusCode) {
      scope.setTag('status_code', String(extra.statusCode))
    }
    if (extra.pathname) {
      scope.setTag('url', extra.pathname)
    }

    Object.entries(extra).forEach(([k, v]) => scope.setExtra(k, v))

    // setFingerprint creates a custom grouping key for Sentry that:
    // 1. Groups similar errors together to reduce noise
    // 2. Separates errors by platform and type for better organization
    // 3. Makes error frequency more visible to help prioritize fixes
    switch (type) {
      case 'http_500':
        // setFingerprint makes error group unique
        scope.setFingerprint(['http_500', extra.platform ?? 'unknown'])
        break
      case 'unexpected':
        scope.setFingerprint(['unexpected', extra.platform ?? 'unknown'])
        break
    }

    Sentry.captureException(sentryErr)
  })
}
